import { memo, useCallback, useMemo, useEffect, useState } from "react";
import { X, Plus, AlertTriangle } from "lucide-react";

const DEFAULT_CATEGORIES = [{ name: '', items: [] }];

const ensureCategories = (prev) => {
    if (prev?.categories?.length) return prev.categories;
    return DEFAULT_CATEGORIES;
};

const normalize = (w) => w.trim().toLowerCase();

// ─── Validation ───

const validateClasser = (categories) => {
    const errors = [];

    // Au moins 2 catégories
    if (categories.length < 2) {
        errors.push('Il faut au moins 2 catégories.');
        return errors;
    }

    // Catégories sans nom
    const emptyNames = categories.filter(c => !c.name?.trim());
    if (emptyNames.length > 0) {
        errors.push('Chaque catégorie doit avoir un nom.');
    }

    // Noms de catégories en double
    const catNames = categories.map(c => normalize(c.name || ''));
    const uniqueNames = new Set(catNames.filter(n => n));
    if (uniqueNames.size < catNames.filter(n => n).length) {
        errors.push('Deux catégories portent le même nom.');
    }

    // Catégories vides
    const emptyCats = categories.filter(c => !c.items?.length);
    if (emptyCats.length > 0) {
        errors.push('Chaque catégorie doit contenir au moins 1 élément.');
    }

    // Tous les éléments (toutes catégories confondues)
    const allItems = categories.flatMap(c => (c.items || []).map(normalize));
    const uniqueItems = new Set(allItems);
    if (uniqueItems.size < allItems.length) {
        errors.push('Un même élément apparaît plusieurs fois.');
    }

    // Élément identique au nom d'une catégorie
    const catNamesSet = new Set(catNames.filter(n => n));
    const conflicting = allItems.find(item => catNamesSet.has(item));
    if (conflicting) {
        errors.push(`L'élément "${conflicting}" porte le même nom qu'une catégorie.`);
    }

    // Au moins 3 éléments au total
    if (allItems.length < 3 && allItems.length > 0) {
        errors.push('Il faut au moins 3 éléments au total pour que l\'exercice ait du sens.');
    }

    return errors;
};

// ─── Composant ───

const ClasserEditor = memo(function ClasserEditor({ data, onDataChange, onValidChange }) {
    const categories = data?.categories?.length ? data.categories : DEFAULT_CATEGORIES;

    const [addError, setAddError] = useState(null);

    const errors = useMemo(() => validateClasser(categories), [categories]);
    const isValid = errors.length === 0;

    useEffect(() => {
        onValidChange(isValid);
    }, [isValid, onValidChange]);

    // Initialiser le state si vide
    const needsInit = !data?.categories?.length;
    if (needsInit && typeof onDataChange === 'function') {
        Promise.resolve().then(() => {
            onDataChange(prev => {
                if (prev?.categories?.length) return prev;
                return { ...prev, categories: DEFAULT_CATEGORIES };
            });
        });
    }

    const updateCategoryName = useCallback((index, name) => {
        setAddError(null);
        onDataChange(prev => {
            const next = [...ensureCategories(prev)];
            next[index] = { ...next[index], name };
            return { ...prev, categories: next };
        });
    }, [onDataChange]);

    const addItemToCategory = useCallback((catIndex, item) => {
        const trimmed = item.trim();
        if (!trimmed) return false;

        const normalized = normalize(trimmed);
        const cats = data?.categories || DEFAULT_CATEGORIES;

        // Doublon avec un élément existant (toutes catégories)
        const allItems = cats.flatMap(c => (c.items || []).map(normalize));
        if (allItems.includes(normalized)) {
            setAddError(`"${trimmed}" est déjà dans une catégorie.`);
            return false;
        }

        // Doublon avec un nom de catégorie
        if (cats.some(c => normalize(c.name || '') === normalized)) {
            setAddError(`"${trimmed}" est le nom d'une catégorie.`);
            return false;
        }

        setAddError(null);
        onDataChange(prev => {
            const next = [...ensureCategories(prev)];
            next[catIndex] = { ...next[catIndex], items: [...(next[catIndex].items || []), trimmed] };
            return { ...prev, categories: next };
        });
        return true;
    }, [onDataChange, data?.categories]);

    const removeItemFromCategory = useCallback((catIndex, itemIndex) => {
        setAddError(null);
        onDataChange(prev => {
            const next = [...ensureCategories(prev)];
            next[catIndex] = { ...next[catIndex], items: (next[catIndex].items || []).filter((_, i) => i !== itemIndex) };
            return { ...prev, categories: next };
        });
    }, [onDataChange]);

    const addCategory = useCallback(() => {
        setAddError(null);
        onDataChange(prev => ({
            ...prev,
            categories: [...ensureCategories(prev), { name: '', items: [] }],
        }));
    }, [onDataChange]);

    const removeCategory = useCallback((index) => {
        setAddError(null);
        onDataChange(prev => ({
            ...prev,
            categories: ensureCategories(prev).filter((_, i) => i !== index),
        }));
    }, [onDataChange]);

    const handleAddInput = useCallback((catIndex, inputRef) => {
        if (!inputRef) return;
        const val = inputRef.value.trim();
        if (!val) return;
        const ok = addItemToCategory(catIndex, val);
        if (ok) inputRef.value = '';
    }, [addItemToCategory]);

    // Compter les éléments pour le compteur
    const totalItems = useMemo(() => categories.reduce((acc, c) => acc + (c.items?.length || 0), 0), [categories]);

    return (
        <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5">
                {categories.map((cat, ci) => (
                    <div key={ci} className="flex-1 border-[1.5px] border-dashed border-gray-300 rounded-lg p-2 sm:p-3 relative group">
                        {categories.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeCategory(ci)}
                                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition shadow-sm"
                            >
                                <X size={10} />
                            </button>
                        )}
                        <input
                            type="text"
                            value={cat.name}
                            onChange={(e) => updateCategoryName(ci, e.target.value)}
                            placeholder="Catégorie"
                            className="w-full text-center text-sm font-medium text-gray-700 placeholder-gray-400 bg-transparent focus:outline-none mb-2"
                        />
                        <div className="flex flex-wrap gap-1 justify-center min-h-[28px]">
                            {(cat.items || []).map((item, ii) => (
                                <span key={ii} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-900">
                                    {item}
                                    <button
                                        type="button"
                                        onClick={() => removeItemFromCategory(ci, ii)}
                                        className="text-blue-400 hover:text-red-500 transition"
                                    >
                                        <X size={10} />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-1 mt-2 justify-center">
                            <input
                                type="text"
                                placeholder="Ajouter..."
                                className="w-full sm:max-w-[120px] px-2 py-1 text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 placeholder-gray-400"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddInput(ci, e.target);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={(e) => {
                                    const input = e.currentTarget.previousElementSibling;
                                    handleAddInput(ci, input);
                                }}
                                className="w-6 h-6 rounded-md flex items-center justify-center bg-blue-50 text-blue-600 hover:bg-blue-100 transition flex-shrink-0"
                            >
                                <Plus size={12} />
                            </button>
                        </div>
                    </div>
                ))}
                {categories.length < 4 && (
                    <button
                        type="button"
                        onClick={addCategory}
                        className="flex items-center justify-center border-[1.5px] border-dashed border-gray-200 rounded-lg px-4 text-gray-400 hover:text-gray-600 hover:border-gray-300 transition min-w-[60px]"
                    >
                        <Plus size={16} />
                    </button>
                )}
            </div>

            {/* Erreur inline (ajout doublon) */}
            {addError && (
                <p className="text-[11px] text-red-500 flex items-center gap-1">
                    <AlertTriangle size={11} className="flex-shrink-0" />
                    {addError}
                </p>
            )}

            {/* Compteur + hint */}
            <div className="flex items-center justify-between">
                <p className="text-[11px] text-gray-400">
                    L'élève verra les éléments mélangés et devra les glisser dans la bonne catégorie.
                </p>
                {totalItems > 0 && (
                    <span className={`text-[10px] font-medium ${totalItems >= 3 ? 'text-emerald-600' : 'text-amber-500'}`}>
                        {totalItems} élément{totalItems > 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {/* Erreurs de validation */}
            {errors.length > 0 && categories.some(c => c.name?.trim() || c.items?.length > 0) && (
                <div className="space-y-1">
                    {errors.map((err, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle size={11} className="text-amber-500 flex-shrink-0" />
                            <p className="text-[11px] text-amber-700">{err}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default ClasserEditor;
